<aside class="sidebar" role="navigation">
  <div class="sidebar__header">
    @if (expanded() && version()) {
      <span class="sidebar__version">{{ version() }}</span>
    }
    <button
      type="button"
      class="sidebar__collapse"
      [title]="(expanded() ? 'general.collapse' : 'general.expand') | translate"
      [attr.aria-expanded]="expanded()"
      (click)="toggleExpanded()"
    >
      <em class="fas" [ngClass]="expanded() ? 'fa-angle-double-left' : 'fa-angle-double-right'"></em>
      @if (expanded()) {
        <span>{{ 'general.collapse' | translate }}</span>
      }
    </button>
  </div>

  @if (expanded()) {
    <div class="sidebar__search">
      <input
        type="text"
        class="sidebar__search-input"
        [ngModel]="searchTerm()"
        (ngModelChange)="onSearchTermChange($event)"
        (keydown.escape)="clearSearch()"
        [attr.placeholder]="'general.search' | translate"
        [attr.aria-label]="'general.search' | translate"
      />
      @if (searchTerm()) {
        <button
          type="button"
          class="sidebar__search-clear"
          (click)="clearSearch()"
          [attr.aria-label]="'general.clearSearch' | translate"
        >
          <em class="fas fa-times"></em>
        </button>
      } @else {
        <em class="fas fa-search sidebar__search-icon"></em>
      }
    </div>
  }

  <nav class="sidebar__nav">
    @if (searchQuery().trim() && variant() !== 'flyout') {
      <!-- In-panel search (single-surface variants): results take over the panel. -->
      <ng-container [ngTemplateOutlet]="searchList"></ng-container>
    } @else {
      @switch (variant()) {
        @case ('drilldown') {
          @if (drillCurrent(); as current) {
            <button type="button" class="sidebar__item sidebar__item--back" (click)="drillBack()">
              <span class="sidebar__item-icon"><em class="fas fa-angle-left"></em></span>
              <span class="sidebar__item-label">{{ current.name }}</span>
            </button>

            @for (child of current.children; track child.key) {
              @if (!child.isButton) {
                <button
                  type="button"
                  class="sidebar__item sidebar__item--sub"
                  [class.sidebar__item--current]="isActive(child)"
                  [class.sidebar__item--current-parent]="isActiveAncestor(child)"
                  [class.sidebar__item--disabled]="child.inProgress"
                  [title]="child.name"
                  (click)="onSubSectionClick(child)"
                >
                  <span class="sidebar__item-label">{{ child.name }}</span>
                  <ng-container
                    [ngTemplateOutlet]="badges"
                    [ngTemplateOutletContext]="{ $implicit: child }"
                  ></ng-container>
                  @if (child.children?.length) {
                    <em class="fas fa-angle-right sidebar__item-caret"></em>
                  }
                </button>
              }
            }

            @if (drilledButtons(); as buttons) {
              @if (buttons.length) {
                <div class="sidebar__drill-buttons">
                  @for (btn of buttons; track btn.key) {
                    <button type="button" class="flyout__button" (click)="navigate(btn)">
                      @if (btn?.navMeta?.iconImg) {
                        <img class="flyout__button--img-button" [src]="btn.navMeta.iconImg" alt="{{ btn.name }}" />
                      } @else {
                        {{ btn.name }}
                      }
                    </button>
                  }
                </div>
              }
            }
          } @else {
            <ng-container [ngTemplateOutlet]="rootList"></ng-container>
          }
        }
        @case ('accordion') {
          @for (section of sections(); track section.key) {
            <div class="sidebar__section">
              @if (expanded()) {
                <div class="sidebar__section-title">{{ section.name }}</div>
              }

              @for (item of section.items; track item.key) {
                <button
                  type="button"
                  class="sidebar__item"
                  [class.sidebar__item--active]="openItemKey() === item.key"
                  [class.sidebar__item--current]="isActive(item)"
                  [class.sidebar__item--current-parent]="isActiveAncestor(item)"
                  [class.sidebar__item--disabled]="item.inProgress"
                  [class.sidebar__item--muted]="item.muted"
                  [title]="item.name"
                  (click)="onTopItemClick(item)"
                >
                  <span class="sidebar__item-icon" [class.sidebar__item-icon--hex]="item.isModule">
                    @if (isImageIcon(item.icon)) {
                      <img [src]="item.icon" [alt]="item.name" />
                    } @else {
                      <em [ngClass]="item.icon || 'fas fa-circle'"></em>
                    }
                  </span>

                  @if (expanded()) {
                    <span class="sidebar__item-label">{{ item.name }}</span>
                    <ng-container
                      [ngTemplateOutlet]="badges"
                      [ngTemplateOutletContext]="{ $implicit: item }"
                    ></ng-container>
                    @if (item.children?.length) {
                      <em
                        class="fas sidebar__item-caret"
                        [ngClass]="openItemKey() === item.key ? 'fa-angle-down' : 'fa-angle-right'"
                      ></em>
                    }
                  }
                </button>

                @if (expanded() && openItemKey() === item.key) {
                  @for (sub of item.children; track sub.key) {
                    @if (!sub.isButton) {
                      @if (sub.children?.length) {
                        <button
                          type="button"
                          class="sidebar__item sidebar__item--sub"
                          [class.sidebar__item--current-parent]="isActiveAncestor(sub)"
                          [class.sidebar__item--disabled]="sub.inProgress"
                          (click)="onSubSectionClick(sub)"
                        >
                          <span class="sidebar__item-label">{{ sub.name }}</span>
                          <em
                            class="fas sidebar__item-caret"
                            [ngClass]="isSubExpanded(sub.key) ? 'fa-angle-down' : 'fa-angle-right'"
                          ></em>
                        </button>

                        @if (isSubExpanded(sub.key)) {
                          @for (leaf of sub.children; track leaf.key) {
                            <button
                              type="button"
                              class="sidebar__item sidebar__item--leaf"
                              [class.sidebar__item--current]="isActive(leaf)"
                              [class.sidebar__item--disabled]="leaf.inProgress"
                              (click)="navigate(leaf)"
                            >
                              <span class="sidebar__item-label">{{ leaf.name }}</span>
                              <ng-container
                                [ngTemplateOutlet]="badges"
                                [ngTemplateOutletContext]="{ $implicit: leaf }"
                              ></ng-container>
                            </button>
                          }
                        }
                      } @else {
                        <button
                          type="button"
                          class="sidebar__item sidebar__item--sub"
                          [class.sidebar__item--current]="isActive(sub)"
                          [class.sidebar__item--disabled]="sub.inProgress"
                          (click)="navigate(sub)"
                        >
                          <span class="sidebar__item-label">{{ sub.name }}</span>
                          <ng-container
                            [ngTemplateOutlet]="badges"
                            [ngTemplateOutletContext]="{ $implicit: sub }"
                          ></ng-container>
                        </button>
                      }
                    }
                  }

                  @if (itemButtons(); as buttons) {
                    @if (buttons.length) {
                      <div class="sidebar__drill-buttons">
                        @for (btn of buttons; track btn.key) {
                          <button type="button" class="flyout__button" (click)="navigate(btn)">
                            @if (btn?.navMeta?.iconImg) {
                              <img
                                class="flyout__button--img-button"
                                [src]="btn.navMeta.iconImg"
                                alt="{{ btn.name }}"
                              />
                            } @else {
                              {{ btn.name }}
                            }
                          </button>
                        }
                      </div>
                    }
                  }
                }
              }
            </div>
          }
        }
        @default {
          <ng-container [ngTemplateOutlet]="rootList"></ng-container>
        }
      }
    }
  </nav>
</aside>

<!-- Resize handle: sibling of .sidebar (which clips overflow-x) so it stays grabbable. -->
@if (expanded()) {
  <div
    class="sidebar__resize"
    role="separator"
    aria-orientation="vertical"
    [attr.aria-label]="'general.resizeSidebar' | translate"
    (pointerdown)="startResize($event)"
  ></div>
}

<!-- Accordion flyout for the open top-level item (flyout variant only) -->
@if (variant() === 'flyout' && openItem(); as item) {
  <div class="flyout" (mouseleave)="closeFlyout()">
    <button type="button" class="flyout__header flyout__header--back" (click)="closeFlyout()">
      <em class="fas fa-angle-left"></em>
      <span>{{ item.name }}</span>
    </button>

    <div class="flyout__body">
      @for (sub of item.children; track sub.key) {
        @if (sub.isButton) {
          <!-- buttons render in the footer below -->
        } @else if (sub.children?.length) {
          <button
            type="button"
            class="flyout__group"
            [class.flyout__group--open]="isSubExpanded(sub.key)"
            [class.flyout__group--current]="isActiveAncestor(sub)"
            [class.flyout__group--disabled]="sub.inProgress"
            (click)="onSubSectionClick(sub)"
          >
            <span class="flyout__group-label">{{ sub.name }}</span>
            <ng-container [ngTemplateOutlet]="badges" [ngTemplateOutletContext]="{ $implicit: sub }"></ng-container>
            <em
              class="fas flyout__group-caret"
              [ngClass]="isSubExpanded(sub.key) ? 'fa-angle-down' : 'fa-angle-right'"
            ></em>
          </button>

          @if (isSubExpanded(sub.key)) {
            <div class="flyout__leaves">
              @for (leaf of sub.children; track leaf.key) {
                <button
                  type="button"
                  class="flyout__leaf"
                  [class.flyout__leaf--current]="isActive(leaf)"
                  [class.flyout__leaf--disabled]="leaf.inProgress"
                  (click)="navigate(leaf)"
                >
                  {{ leaf.name }}
                  <ng-container
                    [ngTemplateOutlet]="badges"
                    [ngTemplateOutletContext]="{ $implicit: leaf }"
                  ></ng-container>
                </button>
              }
            </div>
          }
        } @else {
          <button
            type="button"
            class="flyout__group flyout__leaf"
            [class.flyout__leaf--current]="isActive(sub)"
            [class.flyout__group--disabled]="sub.inProgress"
            (click)="navigate(sub)"
          >
            <span class="flyout__group-label">{{ sub.name }}</span>
            <ng-container [ngTemplateOutlet]="badges" [ngTemplateOutletContext]="{ $implicit: sub }"></ng-container>
          </button>
        }
      }
    </div>

    @if (itemButtons(); as buttons) {
      @if (buttons.length) {
        <div class="flyout__footer">
          @for (btn of buttons; track btn.key) {
            <button type="button" class="flyout__button" (click)="navigate(btn)">
              @if (btn?.navMeta?.iconImg) {
                <img class="flyout__button--img-button" [src]="btn.navMeta.iconImg" alt="{{ btn.name }}" />
              } @else {
                {{ btn.name }}
              }
            </button>
          }
        </div>
      }
    }
  </div>
}

<!-- Global search results (flyout variant only; other variants render in-panel). -->
@if (variant() === 'flyout' && searchQuery().trim()) {
  <div class="flyout flyout--search">
    <div class="flyout__header">{{ 'general.search' | translate }}</div>
    <div class="flyout__body">
      @for (leaf of searchResults(); track leaf.state) {
        <button type="button" class="flyout__leaf" (click)="navigate(leaf)">
          <span class="flyout__leaf-name">
            @for (part of highlight(leaf.name); track $index) {
              @if (part.match) {
                <mark>{{ part.text }}</mark>
              } @else {
                <span>{{ part.text }}</span>
              }
            }
          </span>
          @if (leaf.breadcrumb) {
            <span class="flyout__leaf-crumb">{{ leaf.breadcrumb }}</span>
          }
        </button>
      } @empty {
        <div class="flyout__empty">{{ 'general.noResults' | translate }}</div>
      }
    </div>
  </div>
}

<!-- In-panel search results — single-surface variants (accordion / drill-down). -->
<ng-template #searchList>
  @for (leaf of searchResults(); track leaf.state) {
    <button type="button" class="sidebar__item sidebar__item--result" (click)="navigate(leaf)">
      <span class="sidebar__result-name">
        @for (part of highlight(leaf.name); track $index) {
          @if (part.match) {
            <mark>{{ part.text }}</mark>
          } @else {
            <span>{{ part.text }}</span>
          }
        }
      </span>
      @if (leaf.breadcrumb) {
        <span class="sidebar__result-crumb">{{ leaf.breadcrumb }}</span>
      }
    </button>
  } @empty {
    <div class="flyout__empty sidebar__empty">{{ 'general.noResults' | translate }}</div>
  }
</ng-template>

<!-- Item status badges (New / Soon / Upgrade) — shared across every variant. -->
<ng-template #badges let-item>
  @if (item.isNew) {
    <span class="sidebar__badge sidebar__badge--new">{{ 'general.new' | translate }}</span>
  }
  @if (item.inProgress) {
    <span class="sidebar__badge sidebar__badge--soon">{{ 'general.soon' | translate }}</span>
  }
  @if (item.isUpgradeBadgeEnabled) {
    <span class="sidebar__badge sidebar__badge--upgrade">{{ 'general.upgrade' | translate }}</span>
  }
</ng-template>

<!-- Root section list — shared by the flyout and drill-down (root) variants. -->
<ng-template #rootList>
  @for (section of sections(); track section.key) {
    <div class="sidebar__section">
      @if (expanded()) {
        <div class="sidebar__section-title">{{ section.name }}</div>
      }

      @for (item of section.items; track item.key) {
        <button
          type="button"
          class="sidebar__item"
          [class.sidebar__item--active]="openItemKey() === item.key"
          [class.sidebar__item--current]="isActive(item)"
          [class.sidebar__item--current-parent]="isActiveAncestor(item)"
          [class.sidebar__item--disabled]="item.inProgress"
          [class.sidebar__item--muted]="item.muted"
          [title]="item.name"
          (click)="onTopItemClick(item)"
        >
          <span class="sidebar__item-icon" [class.sidebar__item-icon--hex]="item.isModule">
            @if (isImageIcon(item.icon)) {
              <img [src]="item.icon" [alt]="item.name" />
            } @else {
              <em [ngClass]="item.icon || 'fas fa-circle'"></em>
            }
          </span>

          @if (expanded()) {
            <span class="sidebar__item-label">{{ item.name }}</span>

            <ng-container [ngTemplateOutlet]="badges" [ngTemplateOutletContext]="{ $implicit: item }"></ng-container>
            @if (item.children?.length) {
              <em class="fas fa-angle-right sidebar__item-caret"></em>
            }
          }
        </button>
      }
    </div>
  }
</ng-template>
